import React, { useEffect, useState, useRef } from 'react'
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CharLimitedInput } from '@/components/common/CharLimitedInput'
import { SortableItem } from '@/components/common/SortableItem'

export function QuestionBuilder({ questions: externalQuestions = [], onChange = () => {}, className = '', formErrors = {} }) {
    const [questions, setQuestions] = useState(externalQuestions)
    const [showMenu, setShowMenu] = useState(false)
    const [activeId, setActiveId] = useState(null)
    const sensors = useSensors(useSensor(PointerSensor))
    const menuRef = useRef(null)

    useEffect(() => {
        setQuestions(externalQuestions)
    }, [externalQuestions])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false)
            }
        }

        const handleEsc = (event) => {
            if (event.key === 'Escape') {
                setShowMenu(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEsc)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEsc)
        }
    }, [])

    const sync = (updated) => {
        setQuestions(updated)
        onChange?.(updated)
    }

    const addQuestion = (type) => {
        const questionId = crypto.randomUUID()
        const newQuestion = {
            id: questionId, // Keep for frontend tracking
            key: `custom_${questionId.slice(0, 8)}`, // Facebook API requires unique key
            type: 'CUSTOM',
            label: '', // Will be set from question text
            optional: false,
            collapsed: false,
            question: '',
            options: type === 'multiple_choice' ? ['', ''] : undefined,
        }
        sync([...questions, newQuestion])
    }

    const updateQuestion = (id, updates) => {
        sync(questions.map((q) => (q.id === id ? { ...q, ...updates } : q)))
    }

    const removeQuestion = (id) => {
        sync(questions.filter((q) => q.id !== id))
    }

    const addAnswer = (id) => {
        sync(questions.map((q) => (q.id === id && q.options ? { ...q, options: [...q.options, ''] } : q)))
    }

    const updateAnswer = (qid, idx, val) => {
        sync(
            questions.map((q) =>
                q.id === qid && q.options
                    ? {
                        ...q,
                        options: q.options.map((a, i) => (i === idx ? val : a)),
                    }
                    : q,
            ),
        )
    }

    const removeAnswer = (qid, idx) => {
        sync(
            questions.map((q) =>
                q.id === qid && q.options
                    ? {
                        ...q,
                        options: q.options.filter((_, i) => i !== idx),
                    }
                    : q,
            ),
        )
    }

    const customQuestions = questions.filter((q) => q.type === 'CUSTOM')
    const systemQuestions = questions.filter((q) => q.type !== 'CUSTOM')

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={({ active }) => setActiveId(active.id)}
            onDragEnd={({ active, over }) => {
                setActiveId(null)
                if (active.id !== over?.id) {
                    const oldIndex = customQuestions.findIndex((q) => q.id === active.id)
                    const newIndex = customQuestions.findIndex((q) => q.id === over.id)
                    const reordered = arrayMove(customQuestions, oldIndex, newIndex)
                    const finalQuestions = [...systemQuestions, ...reordered]
                    sync(finalQuestions)
                }
            }}
        >
            <div className="pb-4">
                <SortableContext items={customQuestions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
                    {customQuestions.map((q) => (
                        <SortableItem key={q.id} id={q.id}>
                            {({ listeners }) => (
                                <div className={`${className} border-bottom-white-1 tw:pb-4 tw:pt-4 tw:w-full`}>
                                    <div className="tw:flex tw:items-start tw:justify-between tw:mb-2">
                                        <div className="tw:flex tw:items-center tw:gap-2 tw:text-white font-semibold">
                                            <i
                                                {...listeners}
                                                className="bi bi-grip-vertical tw:text-slate-500 tw:pr-1 tw:cursor-grab tw:active:cursor-grabbing"
                                            />
                                            <button
                                                onClick={() => updateQuestion(q.id, { collapsed: !q.collapsed })}
                                                className="tw:text-blue-400 tw:focus:outline-none"
                                            >
                                                <i
                                                    className={`bi ${
                                                        q.collapsed ? 'bi-chevron-right' : 'bi-chevron-down'
                                                    }`}
                                                />
                                            </button>
                                            <span className="tw:capitalize tw:pl-2">
                                                {q.options?.length > 0 ? 'Multiple choice' : 'Short answer'}
                                            </span>
                                        </div>

                                        <div className="tw:flex tw:items-center tw:gap-3">
                                            <input
                                                type="checkbox"
                                                checked={q.optional}
                                                onChange={() => updateQuestion(q.id, { optional: !q.optional })}
                                            />
                                            <label className="tw:flex tw:text-sm tw:text-white">
                                                <span>Optional</span>
                                            </label>
                                            <button
                                                onClick={() => removeQuestion(q.id)}
                                                className="tw:pl-5 tw:pr-2 tw:text-blue-400 tw:hover:text-red-500"
                                            >
                                                <i className="bi bi-trash" />
                                            </button>
                                        </div>
                                    </div>

                                    {!q.collapsed && (
                                        <>
                                            <div className="tw:pb-1">Question</div>
                                            <div className="tw:relative tw:pb-4">
                                                <CharLimitedInput
                                                    value={q.question}
                                                    onChange={(val) => {
                                                        updateQuestion(q.id, { question: val, label: val })
                                                        if (formErrors[`question_${q.id}_text`]) {
                                                            // Trigger revalidation on change if there are errors
                                                            const event = new CustomEvent('revalidateForm')
                                                            document.dispatchEvent(event)
                                                        }
                                                    }}
                                                    placeholder="Enter your question"
                                                    className={formErrors[`question_${q.id}_text`] ? 'tw:!border-red-500' : ''}
                                                />
                                                {formErrors[`question_${q.id}_text`] && (
                                                    <div className="tw:text-xs tw:text-red-500 tw:pl-1 tw:pt-1 tw:absolute">
                                                        {formErrors[`question_${q.id}_text`]}
                                                    </div>
                                                )}
                                            </div>

                                            {q.options && (
                                                <div className="tw:mt-4">
                                                    <div className="tw:pb-1">Answers</div>
                                                    {q.options.map((a, idx) => {
                                                        const hasError = formErrors[`question_${q.id}_option_${idx}`]
                                                        return (
                                                            <div key={idx} className="tw:flex tw:items-center tw:gap-2 tw:mb-2">
                                                                <CharLimitedInput
                                                                    value={a}
                                                                    onChange={(val) => {
                                                                        updateAnswer(q.id, idx, val)
                                                                        if (hasError) {
                                                                            // Trigger revalidation on change if there are errors
                                                                            const event = new CustomEvent('revalidateForm')
                                                                            document.dispatchEvent(event)
                                                                        }
                                                                    }}
                                                                    placeholder={`Answer ${idx + 1}`}
                                                                    className={hasError ? 'tw:!border-red-500' : ''}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="tw:text-blue-400 tw:hover:text-red-500 tw:px-1"
                                                                    onClick={() => removeAnswer(q.id, idx)}
                                                                >
                                                                    <i className="bi bi-trash" />
                                                                </button>
                                                            </div>
                                                        )
                                                    })}
                                                    <button
                                                        type="button"
                                                        onClick={() => addAnswer(q.id)}
                                                        className="media-buying-button-action tw:flex tw:items-center"
                                                    >
                                                        <i className="bi bi-plus" /> <span>Add Answer</span>
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </SortableItem>
                    ))}
                </SortableContext>

                <DragOverlay>
                    {activeId ? (
                        <div className="tw:w-[300px] tw:bg-blue-900 tw:px-3 tw:py-4 tw:rounded tw:flex tw:items-center tw:gap-2 tw:text-white tw:font-semibold">
                            <i className="bi bi-grip-vertical tw:text-slate-500 pr-1" />
                            {customQuestions.find((q) => q.id === activeId)?.question || 'Untitled Question'}
                        </div>
                    ) : null}
                </DragOverlay>

                <div className="tw:mt-6 tw:pl-1">
                    <div className="tw:pl-3 tw:relative tw:inline-block tw:text-left">
                        <button
                            type="button"
                            className="tw:bg-slate-900 tw:border tw:border-blue-500 tw:text-white tw:px-4 tw:py-2 tw:shadow-lg tw:flex tw:items-center tw:gap-1 tw:rounded-none"
                            onClick={() => setShowMenu((prev) => !prev)}
                        >
                            <i className="bi bi-plus" />
                            Add Question
                            <i className="bi bi-caret-down-fill tw:text-xs tw:ml-1" />
                        </button>

                        {showMenu && (
                            <div
                                ref={menuRef}
                                className="tw:absolute tw:z-100 tw:mt-2 tw:w-64 tw:rounded-none tw:bg-slate-900 tw:border tw:border-blue-500 tw:shadow-xl tw:p-1 tw:space-y-1"
                            >
                                <button
                                    onClick={() => {
                                        addQuestion('multiple_choice')
                                        setShowMenu(false)
                                    }}
                                    className="tw:flex tw:items-start tw:gap-3 tw:rounded-lg tw:px-3 tw:py-2 tw:hover:bg-slate-700 tw:hover:!rounded-md tw:text-left tw:w-full"
                                >
                                    <i className="bi bi-list-ul tw:text-lg tw:text-white tw:mt-0.5" />
                                    <div>
                                        <div className="tw:ext-white tw:font-medium tw:text-sm">Multiple choice</div>
                                        <div className="tw:text-slate-500 tw:text-xs tw:leading-snug">
                                            Use answer choices to categorise, sort and filter leads.
                                        </div>
                                    </div>
                                </button>

                                <button
                                    onClick={() => {
                                        addQuestion('short_answer')
                                        setShowMenu(false)
                                    }}
                                    className="tw:flex tw:items-start tw:gap-3 tw:rounded-lg tw:px-3 tw:py-2 tw:hover:bg-slate-700 tw:hover:!rounded-md tw:text-left tw:w-full"
                                >
                                    <i className="bi bi-text-left tw:text-lg tw:text-white tw:mt-0.5" />
                                    <div className="tw:rounded-md">
                                        <div className="tw:text-white tw:font-medium tw:text-sm">Short answer</div>
                                        <div className="tw:text-slate-500 tw:text-xs tw:leading-snug">
                                            Get additional info in a sentence or two.
                                        </div>
                                    </div>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </DndContext>
    )
}
